"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { Shield, Cloud, Users, Settings, BarChart3, Globe } from "lucide-react";

const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => {
  return (
    <div
      className="group relative bg-white/40 backdrop-blur-xl rounded-3xl border border-white/30 hover:bg-white/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden p-6"
      style={{
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
        animationDelay: `${delay}ms`,
      }}
      role="article"
    >
      {/* Card Glow Effect */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-white/90 to-white transition-opacity duration-500"
        aria-hidden="true"
      ></div>

      <div className="relative">
        <div className="flex items-center mb-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-500 border border-white/30"
            style={{
              background: "linear-gradient(135deg, #1719A8, #3B82F6)",
            }}
            aria-hidden="true"
          >
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>

        <h3 className="text-xl font-bold mb-4 text-[#1E293B] transition-all duration-300 group-hover:text-[#1719A8]">
          {title}
        </h3>

        <p className="leading-relaxed text-[#64748B]">{description}</p>
      </div>

      {/* Bottom Accent Line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
        style={{
          background: "linear-gradient(90deg, #1719A8, #D96F2B, #C9C51A)",
        }}
        aria-hidden="true"
      ></div>
    </div>
  );
};

function ProductFeatures() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Shield,
      title: "Latest Technologies",
      description:
        "Built on cutting-edge technologies and industry best practices with solid business logic.",
    },
    {
      icon: Users,
      title: "User Friendly",
      description:
        "Incredibly easy to use interface designed for both simple and sophisticated business needs.",
    },
    {
      icon: Settings,
      title: "Client Customizations",
      description:
        "Flexible customizations for organizations with single unit or multiple locations.",
    },
    {
      icon: Cloud,
      title: "Cloud Based",
      description:
        "Secure, scalable cloud infrastructure ensuring reliable performance and data protection.",
    },
    {
      icon: Globe,
      title: "International Standard",
      description:
        "Global standard software suitable for airports and airlines worldwide.",
    },
    {
      icon: BarChart3,
      title: "Business Intelligence",
      description:
        "Comprehensive BI reports with statistics and graphs for enhanced decision making.",
    },
  ];

  return (
    <section
      className="relative py-0 overflow-hidden"
      role="main"
      aria-labelledby="features-heading"
    >
      <style>{`
        @keyframes slideIn {
          0% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0px); }
        }
        @keyframes dotFade {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }
        .dot {
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          animation: dotFade 3s ease-in-out infinite;
          box-shadow: 0 0 4px rgba(255, 255, 255, 0.6);
        }
        .dot-pattern-tl {
          position: absolute;
          top: 2rem;
          right: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 8px;
          z-index: 10;
        }
        .dot-pattern-br {
          position: absolute;
          bottom: 2rem;
          left: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 8px;
          z-index: 10;
        }
        .dot-pattern-tl .dot:nth-child(n+1):nth-child(-n+8) { animation-delay: 0s; opacity: 0.9; }
        .dot-pattern-tl .dot:nth-child(n+9):nth-child(-n+16) { animation-delay: 0.2s; opacity: 0.8; }
        .dot-pattern-tl .dot:nth-child(n+17):nth-child(-n+24) { animation-delay: 0.4s; opacity: 0.7; }
        .dot-pattern-tl .dot:nth-child(n+25):nth-child(-n+32) { animation-delay: 0.6s; opacity: 0.6; }
        .dot-pattern-tl .dot:nth-child(n+33):nth-child(-n+40) { animation-delay: 0.8s; opacity: 0.5; }
        .dot-pattern-tl .dot:nth-child(n+41):nth-child(-n+48) { animation-delay: 1s; opacity: 0.4; }
        .dot-pattern-tl .dot:nth-child(n+49):nth-child(-n+56) { animation-delay: 1.2s; opacity: 0.3; }
        .dot-pattern-tl .dot:nth-child(n+57):nth-child(-n+64) { animation-delay: 1.4s; opacity: 0.2; }

        .dot-pattern-br .dot:nth-child(n+1):nth-child(-n+8) { animation-delay: 1.4s; opacity: 0.2; }
        .dot-pattern-br .dot:nth-child(n+9):nth-child(-n+16) { animation-delay: 1.2s; opacity: 0.3; }
        .dot-pattern-br .dot:nth-child(n+17):nth-child(-n+24) { animation-delay: 1s; opacity: 0.4; }
        .dot-pattern-br .dot:nth-child(n+25):nth-child(-n+32) { animation-delay: 0.8s; opacity: 0.5; }
        .dot-pattern-br .dot:nth-child(n+33):nth-child(-n+40) { animation-delay: 0.6s; opacity: 0.6; }
        .dot-pattern-br .dot:nth-child(n+41):nth-child(-n+48) { animation-delay: 0.4s; opacity: 0.7; }
        .dot-pattern-br .dot:nth-child(n+49):nth-child(-n+56) { animation-delay: 0.2s; opacity: 0.8; }
        .dot-pattern-br .dot:nth-child(n+57):nth-child(-n+64) { animation-delay: 0s; opacity: 0.9; }

        /* Mobile responsive dots */
        @media (max-width: 768px) {
          .dot-pattern-tl {
            display: none;
          }
          .dot-pattern-br {
            bottom: 1rem;
            right: 1rem;
            grid-template-columns: repeat(6, 1fr);
            gap: 6px;
          }
          .dot {
            width: 3px;
            height: 3px;
          }
        }
      `}</style>

      {/* Main Container Box */}
      <div className="mx-2 lg:mx-6 xl:mx-8 md:mx-4 sm:mx-2 bg-gradient-to-br from-[#1719A8] to-[#0F1280] rounded-3xl border border-white/20 backdrop-blur-sm relative">
        <div className="relative overflow-hidden">
          {/* Background Gradient */}
          <div className="absolute inset-0" aria-hidden="true" />

          {/* Accent Glow Effects */}
          <div
            className="absolute top-10 left-1/2 transform -translate-x-1/2 w-96 h-32 rounded-full blur-3xl opacity-20 lg:opacity-20 md:opacity-15 sm:opacity-5 lg:w-96 lg:h-32 md:w-72 md:h-24 sm:w-48 sm:h-16"
            style={{
              backgroundColor: "#D96F2B",
            }}
            aria-hidden="true"
          />

          <div
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-80 h-40 rounded-full blur-3xl opacity-15 lg:w-80 lg:h-40 md:w-60 md:h-30 sm:w-40 sm:h-20"
            style={{
              backgroundColor: "#C9C51A",
            }}
            aria-hidden="true"
          />

          {/* Decorative Dots - Top Left */}
          <div className="hidden md:block dot-pattern-tl" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`tl-${i}`} className="dot" />
            ))}
          </div>

          {/* Decorative Dots - Bottom Right */}
          <div className="dot-pattern-br" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`br-${i}`} className="dot" />
            ))}
          </div>

          {/* Content Container */}
          <div className="container mx-auto px-4 py-12 sm:py-8 md:py-8 lg:py-12 relative z-20">
            <div
              className={`
              transition-all duration-1000 ease-out
              ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
            `}
            >
              {/* Header Section */}
              <div className="text-center mb-12 lg:mb-12 md:mb-8 sm:mb-6">
                <div className="inline-flex items-center px-6 py-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-full mb-6 shadow-lg">
                  <span
                    className="w-2 h-2 bg-[#1719A8] rounded-full mr-3 animate-pulse"
                    aria-hidden="true"
                  ></span>
                  <span
                    style={{ color: "#1719A8" }}
                    className="text-xs md:text-sm font-semibold uppercase tracking-wider"
                  >
                    Key Features
                  </span>
                </div>

                <header>
                  <h1
                    id="features-heading"
                    className="text-4xl xl:text-6xl lg:text-5xl md:text-4xl sm:text-2xl font-bold text-white leading-tight font-heading mb-6 lg:mb-6 md:mb-4 sm:mb-3"
                  >
                    BaBe Software <br />
                    <span className="bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] bg-clip-text text-transparent">
                      Core Features
                    </span>
                  </h1>
                  <p className="text-md lg:text-xl md:text-lg sm:text-sm text-white/80 leading-relaxed font-body max-w-3xl mx-auto lg:max-w-3xl md:max-w-2xl sm:max-w-xl">
                    BaBe Software is built on latest technologies and standards,
                    based on industry best practices with solid business logic,
                    designed to be user-friendly and incredibly easy to use.
                  </p>
                </header>
              </div>

              {/* First Half - Image Left + 2+1 Feature Cards Right */}
              <div
                className={`mb-20 lg:mb-20 md:mb-12 sm:mb-8 transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
              >
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-12 md:gap-8 sm:gap-6 items-center">
                  {/* Left Image */}
                  <div className="order-2 lg:order-1 p-3 bg-white  shadow-2xl border border-white/20 backdrop-blur-sm rounded-2xl">
                    <img
                      src="https://cdn.acsdev.in/Vasta/product2.png"
                      alt="BaBe Software Interface"
                      className="w-full h-auto "
                    />
                  </div>

                  {/* Right Feature Cards - 2+1 Format */}
                  <div className="order-1 lg:order-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                      {/* Left Column - 2 Cards Stacked */}
                      <div className="space-y-6">
                        <div role="listitem">
                          <FeatureCard
                            icon={features[0].icon}
                            title={features[0].title}
                            description={features[0].description}
                            delay={100}
                          />
                        </div>
                        <div role="listitem">
                          <FeatureCard
                            icon={features[1].icon}
                            title={features[1].title}
                            description={features[1].description}
                            delay={200}
                          />
                        </div>
                      </div>

                      {/* Right Column - 1 Card Centered */}
                      <div className="flex items-center justify-center">
                        <div role="listitem" className="w-full">
                          <FeatureCard
                            icon={features[2].icon}
                            title={features[2].title}
                            description={features[2].description}
                            delay={300}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Half - 1+2 Feature Cards Left + Image Right */}
              <div
                className={`mb-16 lg:mb-16 md:mb-12 sm:mb-8 transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
              >
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-12 md:gap-8 sm:gap-6 items-center">
                  {/* Left Feature Cards - 1+2 Format */}
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                      {/* Left Column - 1 Card Centered */}
                      <div className="flex items-center justify-center">
                        <div role="listitem" className="w-full">
                          <FeatureCard
                            icon={features[3].icon}
                            title={features[3].title}
                            description={features[3].description}
                            delay={400}
                          />
                        </div>
                      </div>

                      {/* Right Column - 2 Cards Stacked */}
                      <div className="space-y-6">
                        <div role="listitem">
                          <FeatureCard
                            icon={features[4].icon}
                            title={features[4].title}
                            description={features[4].description}
                            delay={500}
                          />
                        </div>
                        <div role="listitem">
                          <FeatureCard
                            icon={features[5].icon}
                            title={features[5].title}
                            description={features[5].description}
                            delay={600}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Image */}
                  <div>
                    <img
                      src="https://cdn.acsdev.in/Vasta/babe.png"
                      alt="BaBe Software Dashboard"
                      className="w-full h-auto rounded-lg shadow-2xl border border-white/20 backdrop-blur-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductFeatures;
